import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Cookies from "js-cookie";
import { BASE_URL } from "src/config/api";
import { IBlog } from "src/@types/blog";
import { IContact } from "src/@types/contact";
import { ITeamMember } from "src/@types/team";
import { IPackage } from "src/@types/package";
import { IGalleryItem } from "src/@types/gallery";
import { IFormSubmitRequest, IFormSubmitResponse } from "src/@types/form-submit";
import { CalculatorInput, CalculatorResponse } from "src/page-components/calculator/types/api";

const getCsrfToken = (): string | undefined => {
  return Cookies.get('csrftoken');
};

const baseQueryWithCsrf = fetchBaseQuery({ 
  baseUrl: BASE_URL,
  credentials: 'include',
  prepareHeaders: (headers, { endpoint, type }) => {
    const isMutation = type === 'mutation';
    
    if (isMutation) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        headers.set('X-CSRFToken', csrfToken);
      } else {
        console.warn(`CSRF token missing for mutation: ${endpoint}`);
      }
    }
    
    return headers;
  },
});

const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  let result = await baseQueryWithCsrf(args, api, extraOptions);
  
  if (result.error && result.error.status === 403) {
    const errorData = result.error.data as any;
    if (errorData?.detail?.includes('CSRF')) {
      const newToken = getCsrfToken();
      if (newToken) {
        result = await baseQueryWithCsrf(args, api, extraOptions);
      }
    }
  }
  
  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getBlogsPosts: builder.query<IBlog[], void>({
      query: () => `/blog-posts/`,
    }),
    getBlogCurrentPost: builder.query<IBlog, string | number>({
      query: (id) => `/blog-posts/${id}/`,
    }),
    getContacts: builder.query<IContact, void>({
      query: () => `/contacts/`,
    }),
    getTeam: builder.query<ITeamMember[], void>({
      query: () => `/team/`,
    }),
    getFOPPackages: builder.query<IPackage[], void>({
      query: () => `/packages/fop`,
    }),
    getLegalEntityPackages: builder.query<IPackage[], void>({
      query: () => `/packages/legal_entity`,
    }),
    submitContactForm: builder.mutation<IFormSubmitResponse, IFormSubmitRequest>({
      query: (body) => ({
        url: `/forms/`,
        method: "POST",
        body,
      }),
    }),
    calculatePrice: builder.mutation<CalculatorResponse, CalculatorInput>({
      query: (body) => ({
        url: `/calculator/`,
        method: "POST",
        body,
      }),
    }),
    getGallery: builder.query<IGalleryItem[], void>({
      query: () => `/gallery/`,
    }),
  }),
});

export const { 
  useGetBlogsPostsQuery, 
  useGetBlogCurrentPostQuery, 
  useGetContactsQuery, 
  useGetTeamQuery, 
  useGetFOPPackagesQuery, 
  useGetLegalEntityPackagesQuery,
  useSubmitContactFormMutation,
  useCalculatePriceMutation,
  useGetGalleryQuery,
} = api;
